/**
 * useResearchSessions - Hook for managing persistent research sessions
 *
 * Handles:
 * - CRUD operations for research sessions
 * - Session list management
 * - Current session state
 * - Message management within sessions
 * - Race condition protection for rapid session switching
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';

export interface ResearchSessionMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  message_type: 'query' | 'verdict' | 'followup' | 'system';
  created_at: string;
  research_history_id?: number | null;
}

export type SessionType = 'general' | 'financial' | 'strategic';

export interface ResearchSessionData {
  id: string;
  customer_id: string;
  title: string | null;
  status: 'active' | 'archived';
  session_type: SessionType;
  created_at: string;
  updated_at: string;
  last_accessed_at: string;
  messages: ResearchSessionMessage[];
  message_count?: number;
}

interface UseResearchSessionsReturn {
  sessions: ResearchSessionData[];
  currentSession: ResearchSessionData | null;
  isLoading: boolean;
  isLoadingSession: boolean;  // Loading state for individual session selection
  pendingSessionId: string | null;  // ID of session currently being loaded
  error: string | null;
  createSession: (initialQuery?: string) => Promise<ResearchSessionData | null>;
  selectSession: (sessionId: string) => Promise<void>;
  clearCurrentSession: () => void;
  addMessage: (
    sessionId: string,
    content: string,
    messageType: 'query' | 'verdict' | 'followup' | 'system',
    role: 'user' | 'assistant' | 'system'
  ) => Promise<ResearchSessionMessage | null>;
  archiveSession: (sessionId: string) => Promise<void>;
  refreshSessions: () => Promise<void>;
}

export function useResearchSessions(sessionType: SessionType = 'general'): UseResearchSessionsReturn {
  const [sessions, setSessions] = useState<ResearchSessionData[]>([]);
  const [currentSession, setCurrentSession] = useState<ResearchSessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ref to track the latest requested session ID for race condition protection
  const latestRequestedSessionRef = useRef<string | null>(null);

  // Fetch all sessions filtered by type
  const refreshSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getResearchSessions({ session_type: sessionType });
      setSessions((response.sessions || []) as ResearchSessionData[]);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch sessions');
    } finally {
      setIsLoading(false);
    }
  }, [sessionType]);

  // Load sessions on mount
  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  // Create a new session with the configured type
  const createSession = useCallback(async (initialQuery?: string): Promise<ResearchSessionData | null> => {
    try {
      setError(null);
      const response = await api.createResearchSession({
        title: initialQuery?.slice(0, 100) || null,
        initial_query: initialQuery || null,
        session_type: sessionType,
      });

      const newSession = response as ResearchSessionData;
      setSessions(prev => [newSession, ...prev]);
      setCurrentSession(newSession);
      return newSession;
    } catch (err) {
      console.error('Failed to create session:', err);
      setError(err instanceof Error ? err.message : 'Failed to create session');
      return null;
    }
  }, [sessionType]);

  // Select a session (fetch full details) with race condition protection
  const selectSession = useCallback(async (sessionId: string) => {
    // Track this as the latest requested session
    latestRequestedSessionRef.current = sessionId;

    // Set loading state immediately
    setIsLoadingSession(true);
    setPendingSessionId(sessionId);
    setError(null);

    try {
      const response = await api.getResearchSession(sessionId);

      // Only update if this is still the latest requested session
      // This prevents race conditions when users rapidly click through sessions
      if (latestRequestedSessionRef.current === sessionId) {
        setCurrentSession(response as ResearchSessionData);
        setIsLoadingSession(false);
        setPendingSessionId(null);
      }
      // If not the latest request, silently discard the response
    } catch (err) {
      // Only show error if this is still the latest request
      if (latestRequestedSessionRef.current === sessionId) {
        console.error('Failed to select session:', err);
        setError(err instanceof Error ? err.message : 'Failed to load session');
        setIsLoadingSession(false);
        setPendingSessionId(null);
      }
    }
  }, []);

  // Add a message to a session
  const addMessage = useCallback(async (
    sessionId: string,
    content: string,
    messageType: 'query' | 'verdict' | 'followup' | 'system',
    role: 'user' | 'assistant' | 'system'
  ): Promise<ResearchSessionMessage | null> => {
    try {
      setError(null);
      const response = await api.addResearchSessionMessage(sessionId, {
        content,
        message_type: messageType,
        role,
      });

      const newMessage = response as ResearchSessionMessage;

      // Update current session with new message
      if (currentSession?.id === sessionId) {
        setCurrentSession(prev => prev ? {
          ...prev,
          messages: [...prev.messages, newMessage],
        } : null);
      }

      // Also update message_count in sessions list so sidebar filter reflects the new count
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, message_count: (s.message_count ?? 0) + 1 } : s
      ));

      return newMessage;
    } catch (err) {
      console.error('Failed to add message:', err);
      setError(err instanceof Error ? err.message : 'Failed to add message');
      return null;
    }
  }, [currentSession]);

  // Archive a session
  const archiveSession = useCallback(async (sessionId: string) => {
    try {
      setError(null);
      await api.archiveResearchSession(sessionId);

      // Remove from list
      setSessions(prev => prev.filter(s => s.id !== sessionId));

      // Clear current if archived
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
      }
    } catch (err) {
      console.error('Failed to archive session:', err);
      setError(err instanceof Error ? err.message : 'Failed to archive session');
    }
  }, [currentSession]);

  // Clear current session (for New Research)
  const clearCurrentSession = useCallback(() => {
    setCurrentSession(null);
  }, []);

  return {
    sessions,
    currentSession,
    isLoading,
    isLoadingSession,
    pendingSessionId,
    error,
    createSession,
    selectSession,
    clearCurrentSession,
    addMessage,
    archiveSession,
    refreshSessions,
  };
}

